import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPages.css';

const TermsPage = () => {
  return (
    <div className="policy-page">
      <div className="policy-container">
        <Link to="/" className="back-link">← Back to SlimBuddy</Link>
        
        <h1>SlimBuddy Terms of Service</h1>
        
        <div className="effective-date">
          <strong>Effective Date:</strong> September 28, 2025<br />
          <strong>Last Updated:</strong> September 28, 2025
        </div>

        <h2>1. Acceptance of Terms</h2>
        <p>By downloading, installing, or using the SlimBuddy mobile application ("App"), you ("User," "you," or "your") agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App.</p>
        
        <p>These Terms constitute a legally binding agreement between you and QFL Capital LLC. ("Company," "we," "us," or "our"). You must be at least 18 years old to use this App.</p>

        <h2>2. Description of Service</h2>
        <p>SlimBuddy is a health and fitness mobile application that provides:</p>
        <ul>
          <li>Personalized nutrition tracking and recommendations</li>
          <li>Exercise logging and fitness monitoring</li>
          <li>AI-powered food recognition and analysis</li>
          <li>Integration with Apple HealthKit for health data synchronization</li>
          <li>Daily health summaries and progress tracking</li>
          <li>Personalized coaching and goal-setting tools</li>
        </ul>

        <h2>3. User Accounts and Registration</h2>
        <h3>Account Creation</h3>
        <ul>
          <li>You must provide accurate, complete, and current information during registration</li>
          <li>You are responsible for maintaining the confidentiality of your account credentials</li>
          <li>You must notify us immediately of any unauthorized use of your account</li>
          <li>One person may not maintain multiple accounts</li>
        </ul>

        <h3>Account Security</h3>
        <ul>
          <li>You are solely responsible for all activities under your account</li>
          <li>We reserve the right to suspend or terminate accounts that violate these Terms</li>
          <li>You must use a secure password and enable recommended security features</li>
        </ul>

        <h2>4. Acceptable Use Policy</h2>
        <h3>Permitted Uses</h3>
        <p>You may use SlimBuddy for personal, non-commercial health and fitness tracking purposes.</p>

        <h3>Prohibited Activities</h3>
        <p>You agree NOT to:</p>
        <ul>
          <li>Use the App for any illegal or unauthorized purpose</li>
          <li>Share, sell, or distribute your account access to others</li>
          <li>Reverse engineer, decompile, or attempt to extract source code</li>
          <li>Upload false, misleading, or harmful health information</li>
          <li>Interfere with or disrupt the App's servers or networks</li>
          <li>Use automated systems (bots, scripts) to access the App</li>
          <li>Violate any applicable laws or regulations</li>
          <li>Harass, abuse, or harm other users or our support staff</li>
        </ul>

        <h2>5. Health Information and Medical Disclaimers</h2>
        <h3>Not Medical Advice</h3>
        <ul>
          <li>SlimBuddy provides general health and fitness information for educational purposes only</li>
          <li className="warning">THE APP IS NOT A SUBSTITUTE FOR PROFESSIONAL MEDICAL ADVICE, DIAGNOSIS, OR TREATMENT</li>
          <li>Always consult qualified healthcare professionals for medical decisions</li>
          <li>Never disregard medical advice or delay seeking treatment based on App content</li>
        </ul>

        <h3>Health Data Accuracy</h3>
        <ul>
          <li>You are responsible for the accuracy of health data you input</li>
          <li>We do not guarantee the accuracy of nutritional information or calorie calculations</li>
          <li>AI-powered features provide estimates that may contain errors</li>
          <li>Individual results may vary based on personal health conditions</li>
        </ul>

        <h3>Emergency Situations</h3>
        <ul>
          <li>Do not use the App for medical emergencies</li>
          <li>Call emergency services (911) for immediate medical assistance</li>
          <li>The App is not designed to detect or prevent medical emergencies</li>
        </ul>

        <h2>6. HealthKit Integration</h2>
        <h3>Apple HealthKit Terms</h3>
        <ul>
          <li>HealthKit integration requires your explicit consent</li>
          <li>You control which health data types are shared with SlimBuddy</li>
          <li>You may revoke HealthKit permissions at any time through iOS Settings</li>
          <li>We comply with Apple's HealthKit Review Guidelines</li>
        </ul>

        <h3>Data Synchronization</h3>
        <ul>
          <li>Health data syncing may have delays or interruptions</li>
          <li>We are not responsible for data loss due to HealthKit connectivity issues</li>
          <li>You should maintain backup copies of important health data</li>
        </ul>

        <h2>7. Intellectual Property Rights</h2>
        <h3>Our Property</h3>
        <ul>
          <li>SlimBuddy owns all rights to the App, including software, design, trademarks, and content</li>
          <li>Our trademarks, logos, and brand elements may not be used without permission</li>
          <li>All AI-generated content and recommendations remain our intellectual property</li>
        </ul>

        <h3>Your Content</h3>
        <ul>
          <li>You retain ownership of personal data and content you upload</li>
          <li>You grant us a license to use your data to provide App services</li>
          <li>You represent that you have rights to all content you upload</li>
        </ul>

        <h2>8. Privacy and Data Protection</h2>
        <p>Your privacy is important to us. Our data collection, use, and sharing practices are governed by our <Link to="/privacy">Privacy Policy</Link>, which is incorporated into these Terms by reference. By using the App, you also agree to our Privacy Policy.</p>

        <h2>9. Subscription and Payment Terms</h2>
        <ul>
          <li>Access to the app features requires a paid subscription</li>
          <li>App Store manages the subscription and is subject to App Store policies and terms of use</li>
          <li>Subscription terms will be clearly disclosed before purchase</li>
        </ul>

        <h3>Billing and Cancellation</h3>
        <ul>
          <li>Subscriptions auto-renew unless cancelled</li>
          <li>Cancellations must be made through your App Store account</li>
          <li>Refunds are subject to App Store policies</li>
          <li>Price changes will be communicated with advance notice</li>
        </ul>

        <h2>10. Limitation of Liability</h2>
        <h3>Service Availability</h3>
        <ul>
          <li>We strive for 24/7 availability but cannot guarantee uninterrupted service</li>
          <li>We may suspend service for maintenance, updates, or technical issues</li>
          <li>We are not liable for service interruptions or data loss</li>
        </ul>

        <h3>Liability Limitations</h3>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
        <ul>
          <li className="warning">TOTAL LIABILITY CAP: Our total liability shall not exceed the amount you paid for the App in the 12 months preceding the claim</li>
          <li className="warning">CONSEQUENTIAL DAMAGES: We are not liable for indirect, incidental, consequential, or punitive damages</li>
          <li className="warning">HEALTH OUTCOMES: We are not responsible for health outcomes, weight loss results, or fitness achievements</li>
          <li className="warning">THIRD-PARTY CONTENT: We are not liable for third-party content, including nutritional databases</li>
        </ul>

        <h2>11. Indemnification</h2>
        <p>You agree to indemnify and hold harmless SlimBuddy and QFL Capital LLC, its owners, officers, directors, employees, and agents from any claims, damages, or expenses arising from:</p>
        <ul>
          <li>Your use of the App</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any third-party rights</li>
          <li>Any false or misleading information you provide</li>
        </ul>

        <h2>12. Termination</h2>
        <h3>Termination Rights</h3>
        <ul>
          <li>You may terminate your account at any time through App settings</li>
          <li>We may suspend or terminate accounts that violate these Terms</li>
          <li>We may discontinue the App with reasonable notice</li>
        </ul>

        <h3>Effect of Termination</h3>
        <ul>
          <li>Upon termination, your right to use the App ceases immediately</li>
          <li>We may retain certain data as required by law or for legitimate business purposes</li>
          <li>Terminated users may not create new accounts without our permission</li>
        </ul>

        <h2>13. Dispute Resolution</h2>
        <h3>Governing Law</h3>
        <p>These Terms are governed by the laws of the United States and the State of Delaware, without regard to conflict of law principles.</p>

        <h3>Arbitration Agreement</h3>
        <p>Any disputes arising from these Terms or the App shall be resolved through binding arbitration administered by the American Arbitration Association, except for:</p>
        <ul>
          <li>Small claims court matters</li>
          <li>Intellectual property disputes</li>
          <li>Injunctive relief requests</li>
        </ul>

        <h3>Class Action Waiver</h3>
        <p>You waive the right to participate in class action lawsuits or class-wide arbitration against SlimBuddy or QFL Capital LLC, its owners, officers, and employees.</p>

        <h2>14. General Provisions</h2>
        <h3>Modifications</h3>
        <ul>
          <li>We may modify these Terms at any time with notice through the App</li>
          <li>Continued use after changes constitutes acceptance of new Terms</li>
          <li>Material changes will require explicit consent</li>
        </ul>

        <h3>Severability</h3>
        <p>If any provision of these Terms is found unenforceable, the remaining provisions remain in full force and effect.</p>

        <h3>Force Majeure</h3>
        <p>We are not liable for delays or failures due to circumstances beyond our reasonable control.</p>

        <h3>Assignment</h3>
        <p>You may not assign these Terms. We may assign our rights and obligations to affiliates or successors.</p>

        <h3>Entire Agreement</h3>
        <p>These Terms and our Privacy Policy constitute the complete agreement between us.</p>

        <h2>15. Contact Information</h2>
        <p>For questions about these Terms:<br />
        Email: info@slimbuddy.ai</p>

        <h2>Apple App Store Additional Terms</h2>
        <p>If you downloaded SlimBuddy from the Apple App Store:</p>
        <ul>
          <li>Apple is not a party to these Terms</li>
          <li>Apple is not responsible for the App or its content</li>
          <li>Apple has no warranty obligations for the App</li>
          <li>Apple is not liable for any claims related to the App</li>
          <li>You may contact Apple for App Store-related issues</li>
        </ul>
      </div>
    </div>
  );
};

export default TermsPage;